import { workerstationService } from "@workerstation/shared/effect/service";
import { domainOperations } from "@workerstation/shared/effect/operation";
import { Context, Effect, Layer, Match, Option, Redacted, Result, Schema } from "effect";
import {
  AuthorizationError, AuthenticatedStationPrincipal, IssuedBrowserStationSession,
  STATION_CSRF_HEADER, STATION_SESSION_COOKIE
} from "./model.ts";
import { ValidatedPlatformIdentity } from "./identity/service.ts";
import { browserSessionCookie } from "./administration/policy.ts";
import { Clock } from "../clock/service.ts";
import { Digest } from "../digest/service.ts";
import { privateDependencyFailure } from "../errors/private-cause.ts";
import { IdEntropy } from "../entropy/service.ts";
import type { RuntimeDependencyError } from "../errors/dependency.ts";
import { StationSessionStorage, StationSessionStorageError, type StationSessionStorageFailure, type StationSessionClient } from "./store.ts";

const domainOperation = domainOperations("Authorization");

export type BrowserSessionFailure = StationSessionStorageFailure | RuntimeDependencyError;
const tokenSchema = IssuedBrowserStationSession.fields.token;
const token = Schema.decodeUnknownOption(tokenSchema);
const cookieValues = Schema.decodeUnknownOption(Schema.Tuple([tokenSchema]));
const url = Schema.decodeUnknownOption(Schema.URLFromString);
const ttl = 8 * 60 * 60_000;
const invalidOutput = () => new StationSessionStorageError({ reason: "invalid-output" });
const denied = (code: AuthorizationError["code"], message: string) => new AuthorizationError(code, message);

/** Exactly one valid host cookie; duplicate cookies never choose an authority. */
export const browserCookieToken = (headers: Headers) => Option.map(cookieValues((headers.get("cookie") ?? "")
  .split(";").map(part => part.trim()).filter(part => part.startsWith(STATION_SESSION_COOKIE + "="))
  .map(part => part.slice(STATION_SESSION_COOKIE.length + 1))), ([value]) => value);

// The old hashes are base64url, not hex. Retain all prefixes and NUL separators.
export const stationCredentialDigest = domainOperation("credentialDigest")(function* (kind: "principal" | "session" | "csrf", input: string) {
  const digest = yield* Digest;
  const namespace = Match.value(kind).pipe(
    Match.when("principal", () => "workerstation-platform-principal:v1"),
    Match.when("session", () => "workerstation-station-session:v1"),
    Match.when("csrf", () => "workerstation-station-csrf:v1"), Match.exhaustive);
  const hex = yield* digest.sha256Utf8(namespace + "\0" + input);
  const bytes = yield* Schema.decodeUnknownEffect(Schema.Uint8ArrayFromHex)(hex).pipe(Effect.mapError(invalidOutput));
  const encoded = yield* Schema.encodeEffect(Schema.Uint8ArrayFromBase64Url)(bytes).pipe(Effect.mapError(invalidOutput));
  return kind + "_" + encoded;
});

export const AuthorizationCredentialError = /* @__PURE__ */ (() => {
  class AuthorizationCredentialError extends privateDependencyFailure<AuthorizationCredentialError>()("AuthorizationCredentialError", "authorization credential") {}
  return AuthorizationCredentialError;
})();
export type AuthorizationCredentialError = InstanceType<typeof AuthorizationCredentialError>;
export const authorizationCredentialError = (cause: unknown) => new AuthorizationCredentialError({ cause: Redacted.make(cause) });

const encoder = new TextEncoder();
const identity = Schema.decodeUnknownResult(ValidatedPlatformIdentity);
const encode = (bytes: ArrayBuffer) => {
  let binary = "";
  for (const byte of new Uint8Array(bytes)) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
};
const hash = (text: string) => Effect.gen(function* () {
  const digest = yield* Digest;
  const bytes = yield* digest.sha256Raw(() => encoder.encode(text)).pipe(
    Effect.mapError(error => authorizationCredentialError(Redacted.value(error.cause)))
  );
  return yield* Effect.try({ try: () => encode(bytes), catch: authorizationCredentialError });
}).pipe(Effect.uninterruptible);

export const platformPrincipalId = domainOperation("platformPrincipalId")(function* (input: ValidatedPlatformIdentity) {
  const validated = yield* Effect.try({ try: () => identity(input), catch: authorizationCredentialError });
  if (Result.isFailure(validated)) return yield* Effect.fail(new AuthorizationError(
    "authentication-required", "A validated platform identity is required."
  ));
  const text = yield* Effect.try({
    try: () => `workerstation-platform-principal:v1\0${input.issuer}\0${input.subject}`,
    catch: authorizationCredentialError
  });
  return `principal_${yield* hash(text)}`;
});

export const hashStationSessionToken = domainOperation("hashSessionToken")(function* (token: string) {
  const text = yield* Effect.try({ try: () => `workerstation-station-session:v1\0${token}`, catch: authorizationCredentialError });
  return `session_${yield* hash(text)}`;
});

export const hashStationCsrfToken = domainOperation("hashCsrfToken")(function* (token: string) {
  const text = yield* Effect.try({ try: () => `workerstation-station-csrf:v1\0${token}`, catch: authorizationCredentialError });
  return `csrf_${yield* hash(text)}`;
});

const equalHash = (left: string, right: string) => {
  const length = Math.max(left.length, right.length);
  return Array.from({ length }, (_, index) => (left.charCodeAt(index % left.length) || 0) ^ (right.charCodeAt(index % right.length) || 0))
    .reduce((different, byte) => different | byte, left.length ^ right.length) === 0;
};

export class BrowserSessions extends workerstationService<BrowserSessions, {
  readonly issue: (stationId: string, identity: ValidatedPlatformIdentity, request: Request) => Effect.Effect<IssuedBrowserStationSession, BrowserSessionFailure>;
  readonly authenticate: (stationId: string, request: Request, allowedOrigins: readonly string[]) => Effect.Effect<AuthenticatedStationPrincipal, BrowserSessionFailure>;
}>()("BrowserSessions") {
  static readonly layer = Layer.effect(BrowserSessions, Effect.gen(function* () {
    const storage = yield* StationSessionStorage, clock = yield* Clock, entropy = yield* IdEntropy, digest = yield* Digest;
    const hash = (kind: "principal" | "session" | "csrf", value: string) => stationCredentialDigest(kind, value).pipe(Effect.provideService(Digest, digest));
    const random = Effect.gen(function* () {
      const bytes = yield* entropy.bytes(32);
      if (bytes.byteLength !== 32) return yield* Effect.fail(invalidOutput());
      return yield* Schema.encodeEffect(Schema.Uint8ArrayFromBase64Url)(bytes).pipe(Effect.mapError(invalidOutput));
    });
    const authenticateToken = domainOperation("authenticateBrowserToken")(function* (client: StationSessionClient, stationId: string, value: string) {
      const sessionHash = yield* hash("session", value);
      const stored = yield* client.session(sessionHash);
      if (Option.isNone(stored) || stored.value.stationId !== stationId || stored.value.transport !== "browser") {
        return yield* Effect.fail(denied("invalid-session", "The station session is invalid."));
      }
      const session = stored.value;
      if (session.revokedAt !== undefined) return yield* Effect.fail(denied("revoked-session", "The station session has been revoked."));
      if (session.expiresAt <= (yield* clock.milliseconds)) return yield* Effect.fail(denied("invalid-session", "The station session has expired."));
      const grant = yield* client.grant(session.principalId);
      if (Option.isNone(grant) || grant.value.revokedAt !== undefined || grant.value.revision < session.grantRevisionAtIssue) {
        return yield* Effect.fail(denied("revoked-grant", "The station role grant is not current."));
      }
      return Object.freeze({ stationId, principalId: session.principalId, platformIssuer: session.platformIssuer,
        platformSubject: session.platformSubject, role: grant.value.role, grantRevision: grant.value.revision, sessionHash, transport: "browser" as const });
    });
    return BrowserSessions.of({
      issue: domainOperation("issueBrowserSession")(function* (stationId, input, request) {
        const client = yield* storage.select(stationId);
        const identity = yield* Schema.decodeUnknownEffect(ValidatedPlatformIdentity)(input).pipe(Effect.mapError(() => denied("authentication-required", "A validated platform identity is required.")));
        const principalId = yield* hash("principal", identity.issuer + "\0" + identity.subject);
        const grant = yield* client.grant(principalId);
        if (Option.isNone(grant) || grant.value.revokedAt !== undefined) return yield* Effect.fail(denied("revoked-grant", "The station role grant is not current."));
        const now = yield* clock.milliseconds;
        const value = yield* random, csrfToken = yield* random;
        const sessionHash = yield* hash("session", value);
        const previous = browserCookieToken(request.headers);
        const previousSessionHash = Option.isNone(previous) ? null : yield* hash("session", previous.value);
        if (identity.expiresAt <= now) return yield* Effect.fail(denied("invalid-assertion-time", "The platform identity has expired."));
        const expiresAt = now + ttl;
        yield* client.rotate(previousSessionHash, {
          stationId, sessionHash, principalId, csrfHash: yield* hash("csrf", csrfToken), transport: "browser",
          platformIssuer: identity.issuer, platformSubject: identity.subject, policySource: identity.policySource,
          grantRevisionAtIssue: grant.value.revision, createdAt: now, expiresAt,
          ...(previousSessionHash === null ? {} : { rotatedFromHash: previousSessionHash, rotatedAt: now })
        }).pipe(Effect.uninterruptible);
        const setCookie = yield* Effect.fromResult(browserSessionCookie(value, ttl / 1000)).pipe(Effect.mapError(invalidOutput));
        return yield* Schema.decodeUnknownEffect(IssuedBrowserStationSession)({ token: value, csrfToken,
          setCookie, expiresAt }).pipe(Effect.mapError(invalidOutput));
      }),
      authenticate: domainOperation("authenticateBrowserRequest")(function* (stationId, request, allowedOrigins) {
        const client = yield* storage.select(stationId);
        const value = browserCookieToken(request.headers);
        if (Option.isNone(value)) return yield* Effect.fail(denied("authentication-required", "A browser session is required."));
        const principal = yield* authenticateToken(client, stationId, value.value);
        if (!["GET", "HEAD", "OPTIONS"].includes(request.method.toUpperCase())) {
          const origin = request.headers.get("origin");
          if (!origin || origin === "null" || !allowedOrigins.some(allowed => Option.exists(url(allowed), parsed => parsed.origin === allowed && allowed === origin))) {
            return yield* Effect.fail(denied("invalid-origin", "The mutation Origin is not allowed."));
          }
          const csrf = token(request.headers.get(STATION_CSRF_HEADER));
          // Preserve the second durable session read. Rotation/revocation can
          // change the CSRF binding after the authentication read.
          const stored = yield* client.session(principal.sessionHash);
          if (Option.isNone(csrf) || Option.isNone(stored) || !stored.value.csrfHash) return yield* Effect.fail(denied("invalid-csrf", "The mutation CSRF proof is invalid."));
          if (!equalHash(yield* hash("csrf", csrf.value), stored.value.csrfHash)) return yield* Effect.fail(denied("invalid-csrf", "The mutation CSRF proof is invalid."));
        }
        return principal;
      })
    });
  }));
}
